import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '../../../shared/services/api.service';
import { DateService } from '../../../shared/services/date.service';
import { ColDef } from 'ag-grid-community';

export interface ESimData {
  id: number;
  created_at: string;
  iccid: string;
  lpa: string;
  imsis: string | null;
  matching_id: string;
  qrcode: string;
  qrcode_url: string;
  direct_apple_installation_url: string;
  voucher_code: string | null;
  airalo_code: string | null;
  apn_type: string;
  apn_value: string | null;
  is_roaming: boolean;
  confirmation_code: string | null;
  brand_settings_name: string | null;
  msisdn: string | null;
  recycled: boolean;
  recycled_at: string | null;
  apn: {
    ios: {
      apn_type: string;
      apn_value: string | null;
    };
    android: {
      apn_type: string;
      apn_value: string | null;
    };
  };
}

export interface ESimsResponse {
  error: boolean;
  data: {
    data: ESimData[];
    links: any;
    meta: any;
  };
  message: string;
}

@Component({
  standalone: false,
  selector: 'app-sims',
  templateUrl: './sims.component.html',
  styleUrl: './sims.component.scss'
})
export class SimsComponent implements OnInit {
  gridApi: any;
  gridColumnApi: any;
  apiUrl: any;
  loading = true;
  error: string | null = null;
  sims: ESimData[] = [];

  constructor(
    private apiService: ApiService,
    private router: Router,
    private _dateService: DateService
  ) {}

  columnDefs: ColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      editable: false,
      filter: true,
      sortable: true,
      width: 100
    },
    {
      headerName: 'ICCID',
      field: 'iccid',
      editable: false,
      filter: true,
      sortable: true,
      width: 200
    },
    {
      headerName: 'LPA',
      field: 'lpa',
      editable: false,
      filter: true,
      sortable: true,
      width: 150
    },
    {
      headerName: 'Matching ID',
      field: 'matching_id',
      editable: false,
      filter: true,
      sortable: true,
      width: 120
    },
    {
      headerName: 'APN Type',
      field: 'apn_type',
      editable: false,
      filter: true,
      sortable: true,
      width: 120
    },
    {
      headerName: 'Roaming',
      field: 'is_roaming',
      editable: false,
      filter: true,
      sortable: true,
      width: 100,
      valueFormatter: (params) => params.value ? 'Yes' : 'No'
    },
    {
      headerName: 'Created At',
      field: 'created_at',
      editable: false,
      filter: true,
      sortable: true,
      width: 180,
      valueFormatter: (params) => this._dateService.getFormattedDateWithAMPM(params.value)
    },
    {
      headerName: 'Actions',
      field: 'actions',
      editable: false,
      filter: false,
      sortable: false,
      width: 150,
      cellRenderer: (params: any) => {
        const button = document.createElement('button');
        button.classList.add('btn', 'btn-sm', 'btn-primary');
        button.innerHTML = '<i class="bi bi-eye-fill"></i>';
        button.addEventListener('click', () => {
          this.viewDetails(params.data);
        });
        return button;
      }
    }
  ];

  gridOptions: any = {
    paginationPageSize: 15,
    pagination: true,
    paginationPageSizeSelector: [15, 20, 50, 100],
    datasource: this.createDataSource(),
    rowSelection: 'single',
    context: {
      componentParent: this,
    },
    defaultColDef: {
      resizable: true,
      filter: true,
      sortable: true
    }
  };

  ngOnInit(): void {
    this.loadSims();
  }

  loadSims(): void {
    // The JWT interceptor will automatically add the token header
    this.apiService.getData('/api/airalo/sims').subscribe({
      next: (response: ESimsResponse) => {
        if (!response.error && response.data && response.data.data) {
          this.sims = response.data.data;
          if (this.gridApi) {
            this.gridApi.setRowData(this.sims);
          } else {
            this.gridOptions.rowData = this.sims;
          }
        } else {
          this.error = 'Failed to load SIMs data';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching SIMs:', err);
        this.error = 'Error loading SIMs';
        this.loading = false;
      }
    });
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
      window.addEventListener('resize', function() {
        setTimeout(function() {
          params.api.sizeColumnsToFit();
        });
      });
    }
  }

  createDataSource() {
    const vm = this;
    const datasource = {
      getRows: (params: any) => {
        const startRow = params.startRow;
        const request = {
          _page: startRow / vm.gridOptions.paginationPageSize + 1,
          _limit: vm.gridOptions.paginationPageSize,
        };
        this.apiUrl = '/api/airalo/sims?page=' + request._page + '&limit=' + request._limit;
        const filterData = vm.gridApi.getFilterModel();
        Object.keys(filterData).forEach(function (k) {
          if (k && filterData[k]) {
            var filterValue = filterData[k].filter;
            if (k === 'id') {
              vm.apiUrl += "&" + k + "_FULLMATCH=" + filterValue;
            } else {
              vm.apiUrl += "&" + k + "_like=" + filterValue;
            }
          }
        });
        vm.apiService.getData(this.apiUrl).subscribe((res: any) => {
          if (res && !res.error && res.data && res.data.data) {
            params.successCallback(res.data.data, res.data.meta.total);
          } else {
            params.failCallback();
          }
        });
      },
    };
    return datasource;
  }

  viewDetails(sim: ESimData): void {
    this.router.navigate(['/app/sims', sim.iccid]);
  }
}